import { FC, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { dynamicFileDownload, slugify } from 'Utils';
import { Colors, ModalProps } from 'Types';
import { ModalBox, ModalBoxFooter, SubmitButton } from 'Elements';
import { QRCodeCanvas } from 'qrcode.react';

interface Props extends ModalProps {
	namespace: string;
	isOpen: boolean;
	value: string;
}

export const ModalBoxQrCode: FC<Props> = ({
	isOpen,
	onClose,
	namespace,
	value
}) => {
	const { t } = useTranslation();
	const [loadingQrImage, setLoadingQrImage] = useState(false);
	const canvasRef = useRef<HTMLCanvasElement>(null);

	const getPic = async () => {
		setLoadingQrImage(true);
		try {
			const canvas = canvasRef.current;
			if (!canvas) throw new Error('Hidden QR Code canvas not found');
			const pngUrl = canvas.toDataURL('image/png');
			dynamicFileDownload(pngUrl, `${slugify(namespace)}-qrcode.png`);
			setLoadingQrImage(false);
		} catch (err) {
			console.error('Error downloading QR code:', err);
			setLoadingQrImage(false);
		}
	};

	return (
		<ModalBox
			isOpen={isOpen}
			onClose={onClose}
			title={t(`title.${namespace}-qrcode`)}>
			<div className="d-flex justify-content-center align-items-center mb-3">
				<QRCodeCanvas
					ref={canvasRef}
					id="qr-code-canvas-hidden-id"
					size={300}
					value={value}
				/>
			</div>

			<p className="text-gray-4 text-center mt-2">
				{t(`text.scan-${namespace}-qrcode`)}
			</p>
			<ModalBoxFooter className="justify-content-center my-3">
				<SubmitButton
					type="button"
					icon="download"
					isSubmitting={loadingQrImage}
					savingLabel={t('button.preparing-qrcode')}
					color={Colors['white-primary']}
					label={t('button.download-qrcode')}
					onClick={() => getPic()}
				/>
			</ModalBoxFooter>
		</ModalBox>
	);
};

ModalBoxQrCode.displayName = 'ModalBoxQrCode';
